import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';

interface ImpersonatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  company: {
    id: string;
    name: string;
    domain: string;
  } | null;
}

interface ImpersonationSession {
  id: string;
  targetUser: ImpersonatedUser;
  startTime: string;
  expiresIn: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonationSession: ImpersonationSession | null;
  startImpersonation: (userId: string, reason?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

interface ImpersonationProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const { token: currentToken, logout } = useAuth();
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonationSession, setImpersonationSession] = useState<ImpersonationSession | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [impersonationToken, setImpersonationToken] = useState<string | null>(null);

  // Auto-expire timer
  const expirationTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Check if current token is an impersonation token on mount
  useEffect(() => {
    const checkImpersonationToken = () => {
      if (!currentToken) return;

      try {
        // Decode JWT to check for impersonation metadata
        const payload = JSON.parse(atob(currentToken.split('.')[1]));
        if (payload.isImpersonating && payload.impersonationSessionId) {
          setIsImpersonating(true);
          setImpersonationToken(currentToken);

          // Calculate expiration time
          const expiresAt = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = expiresAt - now;

          if (timeUntilExpiry > 0) {
            // Set timer to auto-logout when token expires
            expirationTimerRef.current = setTimeout(() => {
              console.log('🔐 Impersonation session expired');
              handleAutoExpiration();
            }, timeUntilExpiry);
          } else {
            // Token already expired
            handleAutoExpiration();
          }
        }
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    };

    checkImpersonationToken();

    return () => {
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
      }
    };
  }, [currentToken]);

  const handleAutoExpiration = useCallback(async () => {
    setIsImpersonating(false);
    setImpersonationSession(null);
    setImpersonationToken(null);
    alert('Impersonation session has expired. Logging you out.');
    await logout();
  }, [logout]);

  const startImpersonation = async (userId: string, reason?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/system-admin/impersonate/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({ reason: reason || 'Support troubleshooting' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start impersonation');
      }

      // Store impersonation token
      setImpersonationToken(data.accessToken);
      setIsImpersonating(true);
      setImpersonationSession(data.impersonationSession);

      // Redirect to main app domain with token in URL
      const currentDomain = window.location.hostname;
      if (currentDomain.startsWith('admin.')) {
        let mainDomain: string;

        // Handle different environments
        if (currentDomain === 'admin.avery.cloud') {
          // Local development
          mainDomain = 'riskninja.avery.cloud';
        } else if (currentDomain === 'admin-dev.riskninja.ai') {
          // AWS dev environment
          mainDomain = 'dev.riskninja.ai';
        } else if (currentDomain === 'admin.riskninja.ai') {
          // AWS production
          mainDomain = 'riskninja.ai';
        } else {
          // Fallback: just remove 'admin.' prefix
          mainDomain = currentDomain.replace('admin.', '');
        }

        // Pass impersonation token and user info via URL parameter
        const redirectUrl = `${window.location.protocol}//${mainDomain}?impersonation_token=${encodeURIComponent(data.accessToken)}&impersonated_user=${encodeURIComponent(JSON.stringify(data.impersonationSession.targetUser))}`;
        window.location.href = redirectUrl;
      }

      console.log('🔐 Impersonation started:', data.impersonationSession);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start impersonation';
      setError(errorMessage);
      console.error('Failed to start impersonation:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const stopImpersonation = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const tokenToUse = impersonationToken || currentToken;

      const response = await fetch(`${API_BASE_URL}/api/system-admin/stop-impersonation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenToUse}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to stop impersonation');
      }

      // Clear impersonation state
      setIsImpersonating(false);
      setImpersonationSession(null);
      setImpersonationToken(null);

      // Clear localStorage
      localStorage.removeItem('riskninja-impersonation-token');
      localStorage.removeItem('riskninja-impersonation-session');

      // Clear expiration timer
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
        expirationTimerRef.current = null;
      }

      console.log('🔐 Impersonation stopped');

      // Redirect to admin portal
      const currentDomain = window.location.hostname;
      if (!currentDomain.startsWith('admin.')) {
        let adminDomain: string;

        // Handle different environments
        if (currentDomain === 'riskninja.avery.cloud') {
          // Local development
          adminDomain = 'admin.avery.cloud';
        } else if (currentDomain === 'dev.riskninja.ai') {
          // AWS dev environment
          adminDomain = 'admin-dev.riskninja.ai';
        } else if (currentDomain === 'riskninja.ai') {
          // AWS production
          adminDomain = 'admin.riskninja.ai';
        } else {
          // Fallback: replace 'riskninja.' with 'admin.'
          adminDomain = currentDomain.replace('riskninja.', 'admin.');
        }

        window.location.href = `${window.location.protocol}//${adminDomain}`;
      } else {
        // Already on admin, just reload
        window.location.reload();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop impersonation';
      setError(errorMessage);
      console.error('Failed to stop impersonation:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value: ImpersonationContextType = {
    isImpersonating,
    impersonationSession,
    startImpersonation,
    stopImpersonation,
    isLoading,
    error
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};
